import { ApiError } from 'utils/apiError';
import { isSSR } from 'utils/isSSR';

type LogData = {
	correlation_id?: string;
	timeFrom: number;
	timestamp?: number;
	[key: string]: unknown;
};

const logDataMap = new Map<string, LogData>();
const MAX_LOG_ENTRIES = 1000;

function checkLogDataMapSize() {
	if (logDataMap.size > MAX_LOG_ENTRIES) {
		const entries = Array.from(logDataMap.entries()).sort((a, b) => (a[1].timestamp ?? 0) - (b[1].timestamp ?? 0));

		const entriesToRemove = logDataMap.size - MAX_LOG_ENTRIES / 2;
		for (let i = 0; i < entriesToRemove; i++) {
			const entry = entries[i];
			if (entry && entry[0]) {
				logDataMap.delete(entry[0]);
			}
		}
	}
}

export function getLogData(correlation_id: string): LogData {
	const logData = logDataMap.get(correlation_id);
	if (logData) {
		const { timestamp, ...logDataWithoutTimestamp } = logData;
		return logDataWithoutTimestamp;
	}
	return { timeFrom: 0 };
}

export function setLogData(correlation_id: string, logData: Partial<LogData>) {
	const dataWithTimestamp = {
		...logData,
		timestamp: logData.timestamp ?? Date.now(),
	};

	logDataMap.set(correlation_id, { ...getLogData(correlation_id), ...dataWithTimestamp });

	checkLogDataMapSize();
}

export function clearLogData(correlation_id: string) {
	logDataMap.delete(correlation_id);
}

const log = isSSR()
	? import.meta.env.DEV
		? console
		: // @ts-expect-error
			((await import('bunyan')).createLogger({
				name: 'astro',
				streams: [
					{
						level: 'info',
						stream: process.stdout,
					},
					{
						level: 'error',
						stream: process.stderr,
					},
				],
			}) ?? console)
	: null;

/**
 * Log an error with the correlationId and requestId
 * @param correlationId   - trace id
 * @param requestId       - request id
 * @param message 		  - error message
 * @param error 		  - error object
 * @param logStatus - response status, default 500
 * @returns
 * @example
 * logError({ correlationId, requestId }, 'Error message', error);
 */
export function logError(
	{ correlationId: correlation_id, requestId: request_id }: { correlationId: string; requestId: string },
	message: string,
	error: unknown,
	logStatus: number = 500,
) {
	const text = `[ ${correlation_id}:${request_id} ] Error : ${message}`;

	if (isSSR()) {
		log.error({
			correlation_id,
			request_id,
			message: text,
			error,
			response_status: error instanceof ApiError ? error.cause.status : logStatus,
			...getLogData(correlation_id),
		});
	} else {
		console.error(`Error : ${message}`, error);
	}
}

/**
 * Log an error with the correlationId and requestId
 * @param correlationId   - trace id
 * @param requestId       - request id
 * @param message 		  - error message
 * @param error 		  - error object
 * @param logStatus - response status, default 500
 * @returns
 * @example
 * logError({ correlationId, requestId }, 'Error message', error);
 */
export function logErrorTimes(
	{ correlationId: correlation_id, requestId: request_id }: { correlationId: string; requestId: string },
	message: string,
	error: unknown,
	logStatus: number = 500,
) {
	const text = `[ ${correlation_id}:${request_id} ] Error : ${message}`;
	let response_status = logStatus;
	if (error instanceof ApiError) {
		response_status = error.cause.status;
	} else if (error instanceof Error && error.name === 'AbortError') {
		response_status = 299;
	}
	const { timeFrom, ...logData } = getLogData(correlation_id);

	if (isSSR()) {
		log.error({
			correlation_id,
			request_id,
			timeFrom,
			duration_ms: performance.now() - timeFrom,
			message: text,
			error,
			response_status,
			...logData,
		});
	} else {
		console.error(`Error : ${message}`, error);
	}
}

export function logger(
	{ correlationId: correlation_id, requestId: request_id }: { correlationId: string; requestId: string },
	message: string,
	...rest: any[]
) {
	const text = `[ ${correlation_id}:${request_id} ]`;

	if (isSSR()) {
		log.info({ correlation_id, request_id, message: text + message, ...getLogData(correlation_id), ...rest });
	} else {
		// eslint-disable-next-line no-console
		console.log(...rest);
	}
}

export function logTimes(
	{ correlationId: correlation_id, requestId: request_id }: { correlationId: string; requestId: string },
	rest: Record<string, any>,
) {
	const message = `[ ${correlation_id}:${request_id} ]`;
	const { timeFrom, ...logData } = getLogData(correlation_id);

	if (isSSR()) {
		log.info({
			correlation_id,
			request_id,
			message,
			timeFrom,
			duration_ms: performance.now() - timeFrom,
			...logData,
			...rest,
		});
	} else {
		// eslint-disable-next-line no-console
		console.log(rest);
	}
}

export function logInfo(message: string, info?: unknown) {
	const text = `: Info : ${message}`;

	if (isSSR()) {
		log.info({ message: text + (info ? JSON.stringify(info) : '') });
	} else {
		// eslint-disable-next-line no-console
		console.info(text, info);
	}
}
